/*
 *   Copyright (c) 2021 Propally.Inc
 *   All rights reserved.
 */

import dayjs = require("dayjs");
import _ = require("lodash");
import { selector, selectorFamily } from "recoil";

import { PROPERTY_TYPE_VALUES } from "../api/mappings/property";

import {
  PropertyHolder,
  SelectedEndingMonth,
  SelectedStartingMonth,
  SelectedPropertyIds,
  PropertySellSimulationState,
  PropertyPriceSimulationState,
  PriceSimulationState,
  DisabledPriceSimulation,
  SelectedPropertyId,
} from "./atoms";
import { MonthlyBalanceSheets } from "./balancesheet_selectors";
import { Properties, SelectedProperties, SelectedProperty } from "./property_selectors";

export interface YearlySimulationResult {
  year: number;
  market_prices: Object;
  loan_amount: number;
  cumulative_cashflow: number;
  cashflow_after_selling: number;
  cumulative_cashflow_after_selling: number;
}
const yearly_simulation_result_prototype: YearlySimulationResult = {
  year: 0,
  market_prices: {},
  loan_amount: 0,
  cumulative_cashflow: 0,
  cashflow_after_selling: 0,
  cumulative_cashflow_after_selling: 0,
};

export const YearlySimulationResults = selectorFamily<Array<YearlySimulationResult>, number>({
  key: "yearly_simulation_results",
  get:
    (property_id) =>
    ({ get }): Array<YearlySimulationResult> => {
      const result: YearlySimulationResult[] = [];
      const start_at = get(SelectedStartingMonth).endOf("year");
      const end_at = get(SelectedEndingMonth).endOf("year");
      const n_years = end_at.diff(start_at, "year");

      const property = get(Properties).find((e) => {
        return e.id === property_id;
      });

      for (let i = 0; i <= n_years; i++) {
        const year = start_at.add(i, "year").endOf("year");
        result.push(_.cloneDeep(yearly_simulation_result_prototype));
        result[i].year = year.year();

        const sheet = get(MonthlyBalanceSheets(property_id)).get(year.format("YYYY/MM"));

        /** price */

        result[i].market_prices[property.property.name + property.property.room_number] = Math.round(
          sheet.simulated_sales_price
        );

        /** loan */

        result[i].loan_amount += Math.round(sheet.remaining_loan_amount);

        /** culmulative_cashflow */

        result[i].cumulative_cashflow += Math.round(
          sheet.cumulative_rent_income +
            sheet.cumulative_sales_amount_by_selling +
            sheet.cumulative_misc_income -
            (sheet.cumulative_expence +
              sheet.cumulative_misc_expence +
              sheet.cumulative_loan_payment +
              sheet.cumulative_expences_at_buying +
              sheet.cumulative_misc_expences_at_buying +
              sheet.cumulative_property_acquisition_tax +
              sheet.cumulative_loan_prepayment_by_selling +
              sheet.cumulative_expenses_by_selling) -
            sheet.cumulative_real_estate_income_taxes
        );

        /** after_selling */

        result[i].cashflow_after_selling += Math.round(
          result[i].market_prices[property.property.name + property.property.room_number] -
            sheet.remaining_loan_amount -
            (sheet.selling_agent_fee + sheet.stamp_tax + sheet.misc_selling_expense + sheet.capital_gain_tax)
        );

        /** price */

        result[i].cumulative_cashflow_after_selling += result[i].cashflow_after_selling + result[i].cumulative_cashflow;
      }
      return result;
    },
});

export const ReducedYearlySimulationResults = selector({
  key: "reduced_yearly_simulation_results",
  get: ({ get }) => {
    const property_ids = get(SelectedPropertyIds);
    const reduced_results: YearlySimulationResult[] = [];

    const start_at = get(SelectedStartingMonth).endOf("year");
    const end_at = get(SelectedEndingMonth).endOf("year");
    const n_years = end_at.diff(start_at, "year");

    for (let i = 0; i <= n_years; i++) {
      const year = start_at.add(i, "year");
      reduced_results.push(_.cloneDeep(yearly_simulation_result_prototype));
      reduced_results[i].year = year.year();

      property_ids.forEach((property_id) => {
        const property_result = get(YearlySimulationResults(property_id));
        for (const key in property_result[i]) {
          if (key === "market_prices") {
            Object.assign(reduced_results[i][key], property_result[i][key]);
          } else if (key !== "year") {
            reduced_results[i][key] += property_result[i][key];
          }
        }
      });
    }
    return reduced_results;
  },
});

export const SelectedPropertySimulationState = selector({
  key: "selected_properties_sells_simulation_states",
  get: ({ get }) => {
    const property = get(SelectedProperty);
    return {
      id: property.id,
      sell_simulation_state: get(PropertySellSimulationState(property.id)),
      price_simulation_state:
        // 保有物件は常にオファーシミュレーション機能をdisabledとする
        property.property.property_type === PROPERTY_TYPE_VALUES.owned
          ? DisabledPriceSimulation
          : get(PropertyPriceSimulationState(property.id)),
    };
  },
});

export const UpdateSelectedPropertyPriceSimulation = selector({
  key: "enable_selected_properties_price_sim",
  get: () => {
    return {};
  },
  set: ({ get, set }, newValue) => {
    const id = get(SelectedPropertyId);
    const before = get(PropertyPriceSimulationState(id));
    // TODO WANTFIX: Recoil does not allow partial update for Selector#set. find some alternatives.
    const partial = newValue as unknown as Partial<PriceSimulationState>;
    const merged = {
      ...before,
      ...partial,
    };
    set(PropertyPriceSimulationState(id), merged);
  },
});
export interface MonthlySimulationResult {
  property_name: string;
  market_price: number;
  bought_price: number;
  property: PropertyHolder;
}

export const SimulationResultOfThisMonth = selector({
  key: "this_month_simulation_results",
  get: ({ get }) => {
    const properties = get(SelectedProperties);
    const data = properties.map((property) => {
      const sheet_of_this_month = get(MonthlyBalanceSheets(property.id)).get(dayjs().format("YYYY/MM"));

      return {
        property_name: property.property.name + property.property.room_number,
        market_price: sheet_of_this_month.simulated_sales_price,
        bought_price: property.property.total_price_of_property,
        property: property,
      };
    });

    const reduced_market_price = data.reduce((accum, d) => {
      return accum + d.market_price;
    }, 0);
    const reduced_bought_price = data.reduce((accum, d) => {
      return accum + d.bought_price;
    }, 0);
    const reduced_balance = reduced_bought_price - reduced_market_price;

    return {
      data: data,
      reduced: {
        market_price: reduced_market_price,
        bought_price: reduced_bought_price,
        balance: reduced_balance,
      },
    };
  },
});

export const AllSimulationResultOfThisMonth = selector({
  key: "all_this_month_simulation_results",
  get: ({ get }) => {
    const properties = get(SelectedProperties);
    const data = properties.map((property) => {
      const sheet_of_this_month = get(MonthlyBalanceSheets(property.id)).get(dayjs().format("YYYY/MM"));

      return {
        property_name: property.property.name + property.property.room_number,
        market_price: sheet_of_this_month.simulated_sales_price,
        bought_price: property.property.total_price_of_property,
        property: property,
      };
    });

    const reduced_market_price = data.reduce((accum, d) => {
      return accum + d.market_price;
    }, 0);
    const reduced_bought_price = data.reduce((accum, d) => {
      return accum + d.bought_price;
    }, 0);
    const reduced_balance = reduced_bought_price - reduced_market_price;

    return {
      data: data,
      reduced: {
        market_price: reduced_market_price,
        bought_price: reduced_bought_price,
        balance: reduced_balance,
      },
    };
  },
});
